import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Typography,
  LinearProgress,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Stack,
} from '@mui/material';
import { 
  CloudUpload, 
  Archive, 
  InsertDriveFile,
  Warning,
  Error as ErrorIcon,
  LocalHospital,
  Assessment,
  Timeline,
  DataUsage
} from '@mui/icons-material';
import JSZip from 'jszip';
import { extractMetadata } from '../../services/images';
// Add this import with the other imports at the top
import { uploadLargeDicom, shouldUseDirectUpload, formatFileSize } from '../../services/directUpload';
import { useThemeMode } from '../../contexts/ThemeModeContext';

const UploadSteps = [
  'Select Files',
  'Review Content', 
  'Patient Information',
  'Study Details',
  'Upload'
];

const EnhancedUploadDialog = ({ 
  open, 
  onClose, 
  onUpload, 
  uploading, 
  error,
  prefillAccession = ''
}) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const cardBg = isDark ? '#1E1E1E' : '#FFFFFF';
  const textColorPrimary = isDark ? '#F1F5F9' : '#0F172A';
  const textColorSecondary = isDark ? '#CBD5E1' : '#475569';
  const cardBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)';

  console.log('EnhancedUploadDialog rendered, open:', open);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [zipAnalysis, setZipAnalysis] = useState(null);
  const [extractingMetadata, setExtractingMetadata] = useState(false);
  const [patientData, setPatientData] = useState({
    patientName: '',
    patientId: '',
    patientBirthDate: '',
    patientSex: '',
    studyDescription: '',
    seriesDescription: '',
    modality: 'CT',
    accessionNumber: '',
    studyDate: new Date().toISOString().split('T')[0],
    clinicalIndication: '',
    referringPhysician: '',
    priority: 'stat'
  });
  
  const [notification, setNotification] = useState({ 
    open: false, 
    message: '', 
    severity: 'info' 
  });
  
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);

  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  useEffect(() => {
    if (open && prefillAccession) {
      setPatientData((prev) => ({ ...prev, accessionNumber: prefillAccession }));
    }
  }, [open, prefillAccession]);

  // Enhanced ZIP metadata extraction
  const isDicomBuffer = (buffer) => {
    if (buffer.byteLength < 132) return false;
    
    const uint8Array = new Uint8Array(buffer);
    const magic = String.fromCharCode(...uint8Array.slice(128, 132));
    if (magic === 'DICM') return true;
    
    // Check for DICOM-like patterns at the beginning
    const dataView = new DataView(buffer);
    try {
      const firstGroup = dataView.getUint16(0, true);
      const commonGroups = [0x0002, 0x0008, 0x0010, 0x0018, 0x0020, 0x0028];
      return commonGroups.includes(firstGroup);
    } catch {
      return false;
    }
  };

  const isPotentialDicomFile = (filename) => {
    const lower = filename.toLowerCase();
    
    // Skip system files
    if (lower.includes('__macosx') || lower.includes('.ds_store') || 
        lower.includes('thumbs.db') || lower.includes('.git')) {
      return false;
    }
    
    // Check extensions
    const dicomExtensions = ['.dcm', '.dicom', '.dic'];
    if (dicomExtensions.some(ext => lower.endsWith(ext))) {
      return true;
    }
    
    // Check for DICOM-like patterns
    const dicomPatterns = [
      /^\d+$/, // Pure numbers
      /^IM[\d_-]+/i, // IM prefix
      /^CT[\d_-]+/i, // CT prefix  
      /^MR[\d_-]+/i, // MR prefix
      /^US[\d_-]+/i, // US prefix
      /^\d+\.dcm?$/i,
      /^[\d_-]+\.dcm?$/i
    ];
    
    return dicomPatterns.some(pattern => pattern.test(filename)) || filename.length > 4;
  };

  // Helper function to clean patient names: trim and replace separators with spaces
  const cleanPatientName = (name) => {
    if (!name) return null;
    // Replace common DICOM separators (^, =, |, etc.) with spaces
    return name.trim().replace(/[\^=|]/g, ' ').replace(/\s+/g, ' ').trim() || null;
  };

  const extractDicomMetadataFromBuffer = async (buffer, filename) => {
    try {
      const dataView = new DataView(buffer);
      let offset = 0;
      
      // Skip preamble if present
      if (buffer.byteLength >= 132) {
        const magic = String.fromCharCode(...new Uint8Array(buffer.slice(128, 132)));
        if (magic === 'DICM') {
          offset = 132;
        }
      }
      
      const tags = new Map();
      const maxOffset = Math.min(dataView.byteLength, offset + 32768);
      
      while (offset < maxOffset - 8) {
        try {
          const group = dataView.getUint16(offset, true);
          const element = dataView.getUint16(offset + 2, true);
          const tag = group.toString(16).padStart(4, '0').toUpperCase() + 
                     element.toString(16).padStart(4, '0').toUpperCase();
          
          offset += 4;
          
          // Simplified VR and length reading
          let length = 0;
          const vr1 = String.fromCharCode(dataView.getUint8(offset));
          const vr2 = String.fromCharCode(dataView.getUint8(offset + 1));
          const possibleVR = vr1 + vr2;
          
          const validVRs = ['AE', 'AS', 'AT', 'CS', 'DA', 'DS', 'DT', 'FL', 'FD', 'IS', 
                           'LO', 'LT', 'OB', 'OF', 'OW', 'PN', 'SH', 'SL', 'SQ', 'SS', 
                           'ST', 'TM', 'UI', 'UL', 'UN', 'US', 'UT'];
          
          if (validVRs.includes(possibleVR)) {
            offset += 2;
            if (['OB', 'OF', 'OW', 'SQ', 'UT', 'UN'].includes(possibleVR)) {
              offset += 2;
              length = dataView.getUint32(offset, true);
              offset += 4;
            } else {
              length = dataView.getUint16(offset, true);
              offset += 2;
            }
          } else {
            length = dataView.getUint32(offset, true);
            offset += 4;
          }
          
          if (length > 0 && length < 1000 && offset + length <= dataView.byteLength) {
            const valueBuffer = new Uint8Array(dataView.buffer.slice(offset, offset + length));
            const value = String.fromCharCode(...valueBuffer).replace(/\0/g, '').trim();
            
            if (value) {
              tags.set(tag, value);
            }
          }
          
          offset += length;
          
        } catch (error) {
          offset += 1;
        }
      }
      
      const parseDicomDate = (dateString) => {
        if (!dateString || dateString.length < 8) return null;
        const clean = dateString.replace(/\D/g, '');
        if (clean.length !== 8) return null;
        const year = clean.substr(0, 4);
        const month = clean.substr(4, 2);
        const day = clean.substr(6, 2);
        try {
          const date = new Date(`${year}-${month}-${day}`);
          return isNaN(date.getTime()) ? null : date.toISOString().split('T')[0];
        } catch {
          return null;
        }
      };

      return {
        filename,
        patientName: cleanPatientName(tags.get('00100010')),
        patientId: tags.get('00100020') || null,
        patientBirthDate: parseDicomDate(tags.get('00100030')),
        patientSex: tags.get('00100040') || null,
        studyInstanceUID: tags.get('0020000D') || null,
        seriesInstanceUID: tags.get('0020000E') || null,
        sopInstanceUID: tags.get('00080018') || null,
        studyDate: parseDicomDate(tags.get('00080020')),
        studyDescription: tags.get('00081030') || null,
        seriesDescription: tags.get('0008103E') || null,
        modality: tags.get('00080060') || null,
        accessionNumber: tags.get('00080050') || null
      };
      
    } catch (error) {
      console.warn(`Failed to extract metadata from ${filename}:`, error);
      return null;
    }
  };

  const analyzeZipContent = async (file) => {
    try {
      setExtractingMetadata(true);
      showNotification('Analyzing ZIP archive and extracting DICOM metadata...', 'info');
      
      const zip = new JSZip();
      const zipContents = await zip.loadAsync(file);
      
      const dicomFiles = [];
      const studies = new Map();
      const series = new Map();
      
      // Process each file in the ZIP
      for (const [filename, fileObj] of Object.entries(zipContents.files)) {
        if (fileObj.dir) continue;
        
        if (isPotentialDicomFile(filename)) {
          try {
            const fileBuffer = await fileObj.async('arraybuffer');
            
            if (isDicomBuffer(fileBuffer)) {
              const metadata = await extractDicomMetadataFromBuffer(fileBuffer, filename);
              if (metadata) {
                dicomFiles.push({
                  filename,
                  metadata,
                  size: fileBuffer.byteLength
                });
                
                // Organize by study and series
                if (metadata.studyInstanceUID) {
                  if (!studies.has(metadata.studyInstanceUID)) {
                    studies.set(metadata.studyInstanceUID, {
                      studyInstanceUID: metadata.studyInstanceUID,
                      patientName: metadata.patientName,
                      patientId: metadata.patientId,
                      studyDate: metadata.studyDate,
                      studyDescription: metadata.studyDescription,
                      modality: metadata.modality,
                      seriesCount: 0,
                      instanceCount: 0
                    });
                  }
                  
                  const study = studies.get(metadata.studyInstanceUID);
                  study.instanceCount++;
                  
                  if (metadata.seriesInstanceUID) {
                    const seriesKey = `${metadata.studyInstanceUID}_${metadata.seriesInstanceUID}`;
                    if (!series.has(seriesKey)) {
                      series.set(seriesKey, {
                        seriesInstanceUID: metadata.seriesInstanceUID,
                        studyInstanceUID: metadata.studyInstanceUID,
                        seriesDescription: metadata.seriesDescription,
                        modality: metadata.modality,
                        instanceCount: 0
                      });
                      study.seriesCount++;
                    }
                    series.get(seriesKey).instanceCount++;
                  }
                }
              }
            }
          } catch (error) {
            console.warn(`Failed to process ${filename}:`, error);
          }
        }
      }
      
      if (dicomFiles.length === 0) {
        throw new Error('No DICOM files found in ZIP archive');
      }
      
      // Get primary metadata from the largest study
      let primaryStudy = null;
      let maxInstances = 0;
      
      for (const study of studies.values()) {
        if (study.instanceCount > maxInstances) {
          maxInstances = study.instanceCount;
          primaryStudy = study;
        }
      }
      
      const representativeFile = dicomFiles.find(file => 
        file.metadata.studyInstanceUID === primaryStudy?.studyInstanceUID
      ) || dicomFiles[0];
      
      return {
        success: true,
        totalFiles: dicomFiles.length,
        studiesCount: studies.size,
        seriesCount: Array.from(studies.values()).reduce((sum, s) => sum + s.seriesCount, 0),
        primaryMetadata: representativeFile?.metadata,
        studies: Array.from(studies.values()),
        series: Array.from(series.values()),
        files: dicomFiles
      };
      
    } catch (error) {
      console.error('ZIP analysis failed:', error);
      return {
        success: false,
        error: error.message
      };
    } finally {
      setExtractingMetadata(false);
    }
  };

  // Enhanced file selection with multiple file support
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setZipAnalysis(null);

    if (files.length > 0) {
      // Auto-generate patient ID if not set
      if (!patientData.patientId) {
        setPatientData(prev => ({
          ...prev,
          patientId: `PAT-${Date.now()}`
        }));
      }

      // Analyze files
      const analysisResults = [];
      let hasZip = false;
      let hasDicom = false;
      let hasImages = false;

      for (const file of files) {
        const extension = file.name.toLowerCase();
        
        if (extension.endsWith('.zip')) {
          hasZip = true;
          try {
            const zipResult = await analyzeZipContent(file);
            analysisResults.push({ file, type: 'zip', analysis: zipResult });

            if (!zipResult.success) {
              showNotification(
                `Could not auto-detect DICOM files in ${file.name}: ${zipResult.error}. You can still fill in Patient Information manually and upload.`,
                'warning'
              );
            }

            if (zipResult.success && zipResult.primaryMetadata) {
              // Pre-populate form with ZIP metadata
              const meta = zipResult.primaryMetadata;
              setPatientData(prev => ({
                ...prev,
                patientName: cleanPatientName(meta.patientName) || prev.patientName,
                patientId: meta.patientId || prev.patientId,
                patientBirthDate: meta.patientBirthDate || prev.patientBirthDate,
                patientSex: meta.patientSex || prev.patientSex,
                studyDescription: meta.studyDescription || prev.studyDescription,
                seriesDescription: meta.seriesDescription || prev.seriesDescription,
                modality: meta.modality || prev.modality,
                accessionNumber: meta.accessionNumber || prev.accessionNumber,
                studyDate: meta.studyDate || prev.studyDate
              }));
            }
          } catch (error) {
            analysisResults.push({ file, type: 'zip', analysis: { success: false, error: error.message } });
          }
        } else if (extension.endsWith('.dcm') || extension.endsWith('.dicom')) {
          hasDicom = true;
          try {
            const metadata = await extractMetadata(file);
            analysisResults.push({ file, type: 'dicom', metadata });
            
            if (metadata && files.length === 1) {
              // Pre-populate for single DICOM file
              setPatientData(prev => ({
                ...prev,
                patientName: cleanPatientName(metadata.patientName) || prev.patientName,
                patientId: metadata.patientId || prev.patientId,
                patientBirthDate: metadata.patientBirthDate || prev.patientBirthDate,
                patientSex: metadata.patientSex || prev.patientSex,
                studyDescription: metadata.studyDescription || prev.studyDescription,
                modality: metadata.modality || prev.modality,
                accessionNumber: metadata.accessionNumber || prev.accessionNumber,
                studyDate: metadata.studyDate || prev.studyDate
              }));
            }
          } catch (error) {
            analysisResults.push({ file, type: 'dicom', metadata: null });
          }
        } else if (['.jpg', '.jpeg', '.png', '.tiff', '.tif'].some(ext => extension.endsWith(ext))) {
          hasImages = true;
          analysisResults.push({ file, type: 'image', willConvert: true });
        } else {
          analysisResults.push({ file, type: 'unknown', error: 'Unsupported format' });
        }
      }

      setZipAnalysis({ results: analysisResults, hasZip, hasDicom, hasImages });

      // Show appropriate notification
      if (hasZip) {
        const totalDicomFiles = analysisResults
          .filter(r => r.type === 'zip' && r.analysis.success)
          .reduce((sum, r) => sum + r.analysis.totalFiles, 0);
        showNotification(
          `ZIP analysis complete! Found ${totalDicomFiles} DICOM files across ${files.length} archive(s).`,
          'success'
        );
      } else if (hasDicom) {
        showNotification(`${files.length} DICOM file(s) selected and analyzed.`, 'success');
      } else if (hasImages) {
        showNotification(`${files.length} image file(s) selected. Will be converted to DICOM.`, 'info');
      }

      // Move to next step if files are valid
      if (analysisResults.some(r => r.type !== 'unknown')) {
        setActiveStep(1);
      }
    }
  };

  const handleInputChange = (field) => (event) => {
    setPatientData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 0: // File selection
        if (selectedFiles.length === 0) {
          errors.files = 'Please select at least one file';
        }
        break;
      case 2: // Patient information
        if (!patientData.patientName) errors.patientName = 'Patient name is required';
        if (!patientData.patientId) errors.patientId = 'Patient ID is required';
        if (!patientData.patientBirthDate) errors.patientBirthDate = 'Patient date of birth is required';
        if (!patientData.patientSex) errors.patientSex = 'Please select patient sex';
        break;
      case 3: // Study details
        if (!patientData.modality) errors.modality = 'Please select modality';
        if (!patientData.studyDate) errors.studyDate = 'Study date is required';
        break;
      default:
        break;
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

const handleUpload = async () => {
  if (!validateStep(2) || !validateStep(3)) {
    showNotification('Please fill in all required fields', 'error');
    return;
  }

  // === ADD THIS SECTION ===
  setUploadProgress(0);

  try {
    // If a single large DICOM file was selected, use direct streaming upload
    if (selectedFiles.length === 1) {
      const selectedFile = selectedFiles[0];
      if (shouldUseDirectUpload(selectedFile)) {
        console.log('Using direct streaming upload for large DICOM file');

        const result = await uploadLargeDicom(
          selectedFile,
          (progress) => {
            setUploadProgress(progress);
          }
        );

        // Pass the result to the parent upload handler and close
        try {
          onUpload && (await onUpload(result));
        } catch (e) {
          // Parent handler may not expect this format; ignore errors here
          console.warn('onUpload handler returned error for direct upload result', e);
        }

        onClose && onClose();
        return;
      }
    }
  } catch (err) {
    console.error('Direct streaming upload failed:', err);
    showNotification('Direct upload failed: ' + (err.message || err), 'error');
    return;
  }
  // === END OF NEW SECTION ===

  setActiveStep(4); // Move to upload step
  
  try {
    let totalResults = [];
    let successfulUploads = 0;
    let failedUploads = 0;
    
    // Upload files one by one
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const formData = new FormData();
      formData.append('image', file);
      
      // Add patient data to each upload
      Object.keys(patientData).forEach(key => {
        if (patientData[key]) {
          formData.append(key, patientData[key]);
        }
      });
      
      try {
        const result = await onUpload(formData);
        
        // ENHANCED: Handle different response types safely
        if (result) {
          if (result.zipFile && result.images) {
            // ZIP file response
            totalResults.push(...result.images);
            successfulUploads++;
            showNotification(
              `ZIP processed: ${result.totalFiles} files, ${result.successful} successful, ${result.failed || 0} failed`, 
              result.failed > 0 ? 'warning' : 'success'
            );
          } else if (result.image) {
            // Single file response
            totalResults.push(result.image);
            successfulUploads++;
            showNotification(
              `File uploaded: ${file.name}`,
              'success'
            );
          } else if (result.message) {
            // Generic success response
            successfulUploads++;
            showNotification(result.message, 'success');
          } else {
            // Unknown response format but upload succeeded
            successfulUploads++;
            showNotification(`Uploaded: ${file.name}`, 'success');
          }
          
          // Handle Orthanc warnings
          if (result.orthancError) {
            showNotification(`Orthanc warning: ${result.orthancError}`, 'warning');
          }
        } else {
          // No result returned
          failedUploads++;
          showNotification(`No response for: ${file.name}`, 'warning');
        }
        
        setUploadProgress(((i + 1) / selectedFiles.length) * 100);
      } catch (fileError) {
        console.error(`Failed to upload ${file.name}:`, fileError);
        failedUploads++;
        
        // Enhanced error message extraction
        let errorMessage = fileError.message;
        if (fileError.response?.data?.error) {
          errorMessage = fileError.response.data.error;
        } else if (fileError.response?.data?.message) {
          errorMessage = fileError.response.data.message;
        }
        
        showNotification(`Failed: ${file.name} - ${errorMessage}`, 'error');
      }
    }
    
    // Show final summary
    if (successfulUploads > 0) {
      const summaryMessage = successfulUploads === selectedFiles.length 
        ? `All ${successfulUploads} files processed successfully!`
        : `${successfulUploads} files succeeded, ${failedUploads} failed`;
      
      showNotification(
        summaryMessage, 
        successfulUploads === selectedFiles.length ? 'success' : 'warning'
      );
    } else {
      showNotification('All uploads failed. Please check file formats and try again.', 'error');
    }
    
    // Auto-close on success
    if (successfulUploads === selectedFiles.length) {
      setTimeout(() => {
        handleClose();
      }, 2000);
    }
    
  } catch (error) {
    console.error('Upload process failed:', error);
    showNotification('Upload process failed: ' + error.message, 'error');
  }
};

  const handleClose = () => {
    if (!uploading && !extractingMetadata) {
      // Reset all state
      setActiveStep(0);
      setSelectedFiles([]);
      setZipAnalysis(null);
      setPatientData({
        patientName: '',
        patientId: '',
        patientBirthDate: '',
        patientSex: '',
        studyDescription: '',
        seriesDescription: '',
        modality: 'CT',
        accessionNumber: '',
        studyDate: new Date().toISOString().split('T')[0],
        clinicalIndication: '',
        referringPhysician: '',
        priority: 'stat'
      });
      setFieldErrors({});
      setUploadProgress(0);
      onClose();
    }
  };

  const getFileIcon = (type, hasError = false) => {
    if (hasError) return <ErrorIcon color="error" />;
    
    switch (type) {
      case 'zip':
        return <Archive color="primary" />;
      case 'dicom':
        return <LocalHospital color="success" />;
      case 'image':
        return <InsertDriveFile color="info" />;
      default:
        return <Warning color="warning" />;
    }
  };

  const getTotalStats = () => {
    if (!zipAnalysis) return null;
    
    let totalFiles = 0;
    let totalStudies = 0;
    let totalSeries = 0;
    
    zipAnalysis.results.forEach(result => {
      if (result.type === 'zip' && result.analysis.success) {
        totalFiles += result.analysis.totalFiles;
        totalStudies += result.analysis.studiesCount;
        totalSeries += result.analysis.seriesCount;
      } else if (result.type === 'dicom') {
        totalFiles += 1;
        totalStudies += 1;
        totalSeries += 1;
      } else if (result.type === 'image') {
        totalFiles += 1;
      }
    });
    
    return { totalFiles, totalStudies, totalSeries };
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0: // File Selection
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select DICOM Files or Archives
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Supported formats: DICOM files (.dcm), ZIP archives (.zip), JPEG (.jpg), PNG (.png), TIFF (.tiff)
            </Typography>
            
            <input
              accept=".dcm,.dicom,.zip,.jpg,.jpeg,.png,.tiff,.tif"
              style={{ display: 'none' }}
              id="file-upload-input"
              type="file"
              multiple
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <label htmlFor="file-upload-input">
              <Button 
                variant="outlined" 
                component="span" 
                disabled={uploading}
                startIcon={<CloudUpload />}
                size="large"
                fullWidth
                sx={{ py: 2, mb: 2 }}
              >
                Select Files
              </Button>
            </label>
            
            {selectedFiles.length > 0 && (
              selectedFiles.length === 1 ? (
                <Alert
                  severity={shouldUseDirectUpload(selectedFiles[0]) ? "warning" : "info"}
                  sx={{ mt: 2, mb: 2 }}
                >
                  <Box>
                    <strong>File:</strong> {selectedFiles[0].name}
                    <br />
                    <strong>Size:</strong> {formatFileSize(selectedFiles[0].size)}
                    {shouldUseDirectUpload(selectedFiles[0]) && (
                      <>
                        <br />
                        <br />
                        ⚡ <strong>Large DICOM file detected</strong>
                        <br />
                        • Using direct streaming to Orthanc
                        <br />
                        • Metadata will be extracted from DICOM tags
                        <br />
                        • Upload may take several minutes
                        <br />
                        <br />
                        <em>Note: For large files, metadata cannot be manually edited during upload.</em>
                      </>
                    )}
                  </Box>
                </Alert>
              ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {selectedFiles.length} file(s) selected
                </Alert>
              )
            )}
          </Box>
        );

      case 1: // Review Content
        const stats = getTotalStats();
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Selected Content
            </Typography>
            
            {stats && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <DataUsage color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h6">{stats.totalFiles}</Typography>
                    <Typography variant="caption">DICOM Images</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Assessment color="secondary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h6">{stats.totalStudies}</Typography>
                    <Typography variant="caption">Studies</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={4}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Timeline color="info" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h6">{stats.totalSeries}</Typography>
                    <Typography variant="caption">Series</Typography>
                  </Paper>
                </Grid>
              </Grid>
            )}
            
            <List>
              {zipAnalysis?.results.map((result, index) => {
                const errorMessage = result.type === 'zip'
                  ? (!result.analysis.success ? result.analysis.error : null)
                  : (result.type === 'dicom' && !result.metadata ? 'Could not read DICOM metadata from this file' : null) || result.error;
                return (
                <ListItem key={index} divider>
                  <ListItemIcon>
                    {getFileIcon(result.type, !!errorMessage)}
                  </ListItemIcon>
                  <ListItemText
                    primary={result.file.name}
                    secondary={
                      <Stack spacing={0.5}>
                        <Typography variant="caption">
                          Size: {(result.file.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                        {result.type === 'zip' && result.analysis.success && (
                          <Typography variant="caption" color="success.main">
                            {result.analysis.totalFiles} DICOM files, {result.analysis.studiesCount} studies
                          </Typography>
                        )}
                        {result.type === 'dicom' && result.metadata && (
                          <Typography variant="caption" color="primary.main">
                            DICOM file ready for upload
                          </Typography>
                        )}
                        {result.type === 'image' && (
                          <Typography variant="caption" color="info.main">
                            Will be converted to DICOM
                          </Typography>
                        )}
                        {errorMessage && (
                          <Typography variant="caption" color="error.main">
                            {errorMessage} — you can still fill in the details manually on the next steps.
                          </Typography>
                        )}
                      </Stack>
                    }
                  />
                </ListItem>
                );
              })}
            </List>
          </Box>
        );

      case 2: // Patient Information
        return (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: textColorPrimary, mb: 1, fontSize: '0.85rem' }}>
              Patient Information
            </Typography>
            
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  label="Patient Name"
                  value={patientData.patientName}
                  onChange={handleInputChange('patientName')}
                  error={!!fieldErrors.patientName}
                  helperText={fieldErrors.patientName || "Format: Last^First^Middle"}
                  placeholder="Doe^John^M"
                  FormHelperTextProps={{ sx: { fontSize: '0.7rem', mx: 0.5, mt: 0.25 } }}
                  InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
                  InputProps={{ sx: { height: 32, fontSize: '0.8rem' } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  label="Patient ID"
                  value={patientData.patientId}
                  onChange={handleInputChange('patientId')}
                  error={!!fieldErrors.patientId}
                  helperText={fieldErrors.patientId || "Unique patient identifier"}
                  FormHelperTextProps={{ sx: { fontSize: '0.7rem', mx: 0.5, mt: 0.25 } }}
                  InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
                  InputProps={{ sx: { height: 32, fontSize: '0.8rem' } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Patient Birth Date"
                  value={patientData.patientBirthDate}
                  onChange={handleInputChange('patientBirthDate')}
                  InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem', color: textColorSecondary } }}
                  InputProps={{ sx: { height: 32, fontSize: '0.8rem' } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required error={!!fieldErrors.patientSex}>
                  <InputLabel sx={{ fontSize: '0.8rem', color: textColorSecondary }}>Patient Sex</InputLabel>
                  <Select
                    value={patientData.patientSex || ''}
                    label="Patient Sex"
                    onChange={handleInputChange('patientSex')}
                    sx={{ height: 32, fontSize: '0.8rem' }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.8rem' }}>Select...</MenuItem>
                    <MenuItem value="M" sx={{ fontSize: '0.8rem' }}>Male</MenuItem>
                    <MenuItem value="F" sx={{ fontSize: '0.8rem' }}>Female</MenuItem>
                    <MenuItem value="O" sx={{ fontSize: '0.8rem' }}>Other</MenuItem>
                  </Select>
                  {fieldErrors.patientSex && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.25, ml: 1, fontSize: '0.7rem' }}>
                      {fieldErrors.patientSex}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Referring Physician"
                  value={patientData.referringPhysician}
                  onChange={handleInputChange('referringPhysician')}
                  placeholder="Dr. Smith"
                  InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
                  InputProps={{ sx: { height: 32, fontSize: '0.8rem' } }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Clinical Indication"
                  multiline
                  rows={2}
                  value={patientData.clinicalIndication}
                  onChange={handleInputChange('clinicalIndication')}
                  placeholder="Reason for examination..."
                  InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
                  InputProps={{ sx: { fontSize: '0.8rem' } }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 3: // Study Details
        return (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: textColorPrimary, mb: 1, fontSize: '0.85rem' }}>
              Study Information
            </Typography>
            
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  required
                  type="date"
                  label="Study Date"
                  value={patientData.studyDate}
                  onChange={handleInputChange('studyDate')}
                  error={!!fieldErrors.studyDate}
                  helperText={fieldErrors.studyDate}
                  InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem', color: textColorSecondary } }}
                  InputProps={{ sx: { height: 32, fontSize: '0.8rem' } }}
                  FormHelperTextProps={{ sx: { fontSize: '0.7rem', mx: 0.5, mt: 0.25 } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required error={!!fieldErrors.modality}>
                  <InputLabel sx={{ fontSize: '0.8rem', color: textColorSecondary }}>Modality</InputLabel>
                  <Select
                    value={patientData.modality}
                    label="Modality"
                    onChange={handleInputChange('modality')}
                    sx={{ height: 32, fontSize: '0.8rem' }}
                  >
                    <MenuItem value="CT" sx={{ fontSize: '0.8rem' }}>CT - Computed Tomography</MenuItem>
                    <MenuItem value="MR" sx={{ fontSize: '0.8rem' }}>MR - Magnetic Resonance</MenuItem>
                    <MenuItem value="XR" sx={{ fontSize: '0.8rem' }}>XR - X-Ray</MenuItem>
                    <MenuItem value="US" sx={{ fontSize: '0.8rem' }}>US - Ultrasound</MenuItem>
                    <MenuItem value="CR" sx={{ fontSize: '0.8rem' }}>CR - Computed Radiography</MenuItem>
                    <MenuItem value="DR" sx={{ fontSize: '0.8rem' }}>DR - Digital Radiography</MenuItem>
                    <MenuItem value="MG" sx={{ fontSize: '0.8rem' }}>MG - Mammography</MenuItem>
                    <MenuItem value="PT" sx={{ fontSize: '0.8rem' }}>PT - PET</MenuItem>
                    <MenuItem value="NM" sx={{ fontSize: '0.8rem' }}>NM - Nuclear Medicine</MenuItem>
                    <MenuItem value="OT" sx={{ fontSize: '0.8rem' }}>OT - Other</MenuItem>
                  </Select>
                  {fieldErrors.modality && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.25, ml: 1, fontSize: '0.7rem' }}>
                      {fieldErrors.modality}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '0.8rem', color: textColorSecondary }}>Priority</InputLabel>
                  <Select
                    value={patientData.priority}
                    label="Priority"
                    onChange={handleInputChange('priority')}
                    sx={{ height: 32, fontSize: '0.8rem' }}
                  >
                    <MenuItem value="stat" sx={{ fontSize: '0.8rem' }}>STAT (within 1 hr)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Accession Number"
                  value={patientData.accessionNumber}
                  onChange={handleInputChange('accessionNumber')}
                  placeholder="Study accession number"
                  InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
                  InputProps={{ sx: { height: 32, fontSize: '0.8rem' } }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Study Description"
                  value={patientData.studyDescription}
                  onChange={handleInputChange('studyDescription')}
                  placeholder="e.g., Chest CT with contrast"
                  InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
                  InputProps={{ sx: { height: 32, fontSize: '0.8rem' } }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Series Description"
                  value={patientData.seriesDescription}
                  onChange={handleInputChange('seriesDescription')}
                  placeholder="e.g., Axial images"
                  InputLabelProps={{ sx: { fontSize: '0.8rem', color: textColorSecondary } }}
                  InputProps={{ sx: { height: 32, fontSize: '0.8rem' } }}
                />
              </Grid>
            </Grid>
          </Box>
        );

      case 4: // Upload Progress
        return (
          <Box textAlign="center">
            <Typography variant="h6" gutterBottom>
              Uploading Files
            </Typography>
            
            <Box sx={{ mt: 3, mb: 3 }}>
              <LinearProgress 
                variant="determinate" 
                value={uploadProgress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {uploadProgress.toFixed(0)}% Complete
              </Typography>
            </Box>
            
            <Typography variant="body2" color="textSecondary">
              {selectedFiles.length > 1 
                ? `Processing ${selectedFiles.length} files...`
                : 'Processing file...'}
            </Typography>
            
            {zipAnalysis?.hasZip && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Extracting and uploading DICOM files from ZIP archives...
              </Typography>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth={false} 
      disableEscapeKeyDown={uploading}
      PaperProps={{
        sx: {
          width: { xs: '95vw', sm: '700px', md: '740px' },
          maxWidth: '760px !important',
          maxHeight: '85vh',
          bgcolor: cardBg,
          color: textColorPrimary,
          borderRadius: '12px',
          border: cardBorder,
          boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: cardBorder }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <CloudUpload color="primary" sx={{ fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: textColorPrimary, fontSize: '0.95rem' }}>
              Upload DICOM Studies
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: textColorSecondary, fontSize: '0.75rem', fontWeight: 600 }}>
            Step {activeStep + 1} of {UploadSteps.length}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ px: 2, py: 1.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.25, px: 1.25, fontSize: '0.75rem' }}>
            {error}
          </Alert>
        )}
        
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={() => setNotification({ ...notification, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setNotification({ ...notification, open: false })} 
            severity={notification.severity}
            sx={{ width: '100%', py: 0.5, px: 1.5, fontSize: '0.78rem' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
        
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2, '& .MuiStepLabel-label': { fontSize: '0.74rem' } }}>
          {UploadSteps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 220 }}>
          {renderStepContent(activeStep)}
        </Box>
        
        {extractingMetadata && (
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress sx={{ height: 4, borderRadius: 2 }} />
            <Typography variant="caption" sx={{ color: textColorSecondary, mt: 0.5, display: 'block', fontSize: '0.72rem' }}>
              Analyzing ZIP archives and extracting DICOM metadata...
            </Typography>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 2, py: 1.25, borderTop: cardBorder }}>
        <Button onClick={handleClose} disabled={uploading || extractingMetadata} sx={{ height: 30, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 1.5 }}>
          Cancel
        </Button>
        
        {activeStep > 0 && activeStep < 4 && (
          <Button onClick={handleBack} disabled={uploading || extractingMetadata} sx={{ height: 30, fontSize: '0.8rem', borderRadius: '6px', textTransform: 'none', px: 1.5 }}>
            Back
          </Button>
        )}
        
        {activeStep < 3 && (
          <Button
            onClick={handleNext}
            disabled={
              uploading || 
              extractingMetadata || 
              (activeStep === 0 && selectedFiles.length === 0)
            }
            variant="contained"
            sx={{
              height: 30,
              fontSize: '0.8rem',
              borderRadius: '6px',
              textTransform: 'none',
              px: 1.75,
              bgcolor: '#0D9488',
              '&:hover': { bgcolor: '#0F766E' }
            }}
          >
            Next
          </Button>
        )}
        
        {activeStep === 3 && (
          <Button
            onClick={handleUpload}
            disabled={uploading || extractingMetadata}
            variant="contained"
            startIcon={<CloudUpload sx={{ fontSize: 16 }} />}
            sx={{
              height: 30,
              fontSize: '0.8rem',
              borderRadius: '6px',
              textTransform: 'none',
              px: 1.75,
              bgcolor: '#0D9488',
              '&:hover': { bgcolor: '#0F766E' }
            }}
          >
            {uploading ? 'Uploading...' : 'Upload Files'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnhancedUploadDialog;
